import React from 'react'
import { CircularProgress } from '@mui/material'
import { fetchQrPdf } from './qrRegistration'
import s from './QrRegistrationV2.module.css'

/*
 * The printed poster before it exists — a copy of the page the backend
 * renders to PDF (AuthService#generateQrRegistrationPdf: the background
 * under a dark scrim, "Scan to sign up", the white QR card, the label, the
 * validity line, the link), drawn at A4 size and scaled down. The QR itself
 * is only made by the backend, so a placeholder stands in for it.
 */
const A4 = { w: 794, h: 1123 }

export function QrPosterMock({ background, label, validity, width = 300 }: {
  background: string | null
  label: string
  validity: string
  width?: number
}) {
  const scale = width / A4.w
  const bg = background
    ? { backgroundImage: `url(${background})`, backgroundSize: 'cover', backgroundPosition: 'center' }
    : { background: 'radial-gradient(circle at 15% 10%, rgba(92,116,180,.35), transparent 34%), linear-gradient(145deg, #121A32 0%, #0F182E 45%, #0B1126 100%)' }
  return (
    <div className={s.posterFrame} style={{ width, height: A4.h * scale }}>
      <div className={s.poster} style={{ width: A4.w, height: A4.h, transform: `scale(${scale})`, ...bg }}>
        <div className={s.posterScrim} />
        <div className={s.posterContent}>
          <h1>Scan to sign up</h1>
          <p className={s.posterSubtitle}>Point your phone&apos;s camera at the code below</p>
          <div className={s.posterCard}>
            <div className={s.qrPlaceholder}>
              <i /><i /><i />
              <span>The QR code is made<br />when the link is created</span>
            </div>
          </div>
          {label && <p className={s.posterLabel}>{label}</p>}
          {validity && <p className={s.posterValidity}>{validity}</p>}
          <p className={s.posterUrl}>https://…/join/…</p>
        </div>
      </div>
    </div>
  )
}

/** the real poster: the backend's PDF, shown in the page; `onBlob` hands it over for download without a second render */
export function QrPdfView({ id, onBlob }: { id: number; onBlob?: (blob: Blob | null) => void }) {
  const [url, setUrl] = React.useState<string | null>(null)
  const [error, setError] = React.useState(false)
  const onBlobRef = React.useRef(onBlob)
  onBlobRef.current = onBlob

  React.useEffect(() => {
    let alive = true
    let objectUrl: string | null = null
    setUrl(null)
    setError(false)
    fetchQrPdf(id)
      .then((blob) => {
        if (!alive) return
        objectUrl = URL.createObjectURL(blob)
        setUrl(objectUrl)
        onBlobRef.current?.(blob)
      })
      .catch(() => { if (alive) { setError(true); onBlobRef.current?.(null) } })
    return () => {
      alive = false
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [id])

  if (error) return <div className={s.pdfState}>The poster could not be made — try again later.</div>
  if (!url) return <div className={s.pdfState}><CircularProgress size={22} /><span>Rendering the poster…</span></div>
  return <iframe className={s.pdf} src={`${url}#toolbar=0&navpanes=0&view=FitH`} title="QR poster" />
}
